/**
 * API client — handles CSRF, cookie auth, automatic token refresh, and retry.
 *
 * Security model:
 * - Auth tokens live in HTTPOnly cookies (set by backend, never touched by JS).
 * - CSRF token is received via X-CSRF-Token response header and stored in memory.
 * - CSRF token is NEVER stored in cookies or localStorage.
 * - On 401: attempt one silent refresh, then retry the original request.
 * - If refresh fails: redirect to login.
 */

import { z } from "zod";

// Same-origin: /api/* is rewritten to the backend by Next.js (see
// next.config.ts). The browser never makes a cross-origin request, so
// cookies stay host-only on the FE domain.
const API_BASE = "";
const API_PREFIX = "/api/v1";

// Per-request timeout. Browser fetch has no default timeout, so a stalled
// connection hangs indefinitely — freezing any UI gated on isLoading.
const REQUEST_TIMEOUT_MS = 15_000;

// ── CSRF token management (in-memory only) ───────────────

let _csrfToken: string | null = null;

/** Get the current CSRF token from memory. */
function getCSRFToken(): string | null {
  return _csrfToken;
}

/** Store a CSRF token in memory. Called after login/refresh. */
export function setCSRFToken(token: string | null): void {
  _csrfToken = token;
}

/** Extract and store CSRF token from a response's X-CSRF-Token header. */
export function extractCSRFToken(response: Response): void {
  const token = response.headers.get("X-CSRF-Token");
  if (token) {
    _csrfToken = token;
  }
}

// ── Logout invalidation ──────────────────────────────────
// Bumped by `logout()` so any pending-retry request can detect a logout
// happened during its lifetime and abort instead of mutating with stale
// state. Active controllers are tracked so logout can also abort fetches
// that are still waiting on the network.

let _logoutEpoch = 0;
const _activeControllers = new Set<AbortController>();

function getLogoutEpoch(): number {
  return _logoutEpoch;
}

/**
 * Mark all in-flight requests as invalidated. Called by `logout()` after
 * the logout request itself has returned. Aborts active fetches and
 * causes any pending refresh/retry path to throw `logged_out`.
 */
export function bumpLogoutEpoch(): void {
  _logoutEpoch += 1;
  for (const controller of _activeControllers) {
    try {
      controller.abort();
    } catch {
      // Controller may already have been aborted by its own timeout.
    }
  }
  _activeControllers.clear();
}

// ── Core fetch wrapper ───────────────────────────────────

export class APIClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body?: unknown,
    /** Seconds until the client may retry. Present on 429 responses. */
    public readonly retryAfter?: number,
  ) {
    super(message);
    this.name = "APIClientError";
  }
}

interface RequestOptions {
  method?: string;
  /**
   * Request body. JSON-serialized by default; ``FormData`` and ``Blob`` are
   * passed through unchanged so the browser sets the correct
   * multipart/binary Content-Type.
   */
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip automatic 401 refresh + retry. Used internally for the refresh call itself. */
  skipRefresh?: boolean;
  /**
   * Caller-supplied abort signal. Chained with the internal timeout /
   * logout controllers — whichever fires first cancels the request.
   * Needed for long-running responses (e.g. SSE streams) that don't fit
   * the default JSON timeout and want explicit caller control.
   */
  signal?: AbortSignal;
  /**
   * Override the default request timeout (ms). The SSE stream consumer
   * sets this to 0 to opt out — the stream itself decides when it's
   * done, the per-request timeout would just cut it short.
   */
  timeoutMs?: number;
}

async function rawFetch(
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  const {
    method = "GET",
    body,
    headers = {},
    skipRefresh = false,
    signal: callerSignal,
    timeoutMs: timeoutOverride,
  } = options;

  const url = `${API_BASE}${API_PREFIX}${path}`;
  const startEpoch = getLogoutEpoch();

  // FormData bodies need to keep their auto-generated multipart boundary,
  // which the browser only emits when Content-Type is *not* preset. Same
  // for raw Blob bodies (file downloads/uploads). Anything else stays JSON.
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const isBlob = typeof Blob !== "undefined" && body instanceof Blob;

  const requestHeaders: Record<string, string> = { ...headers };
  if (!isFormData && !isBlob) {
    requestHeaders["Content-Type"] = requestHeaders["Content-Type"] ?? "application/json";
  }

  // Attach CSRF token for mutating requests
  if (method !== "GET" && method !== "HEAD") {
    const csrf = getCSRFToken();
    if (csrf) {
      requestHeaders["X-CSRF-Token"] = csrf;
    }
  }

  const controller = new AbortController();
  _activeControllers.add(controller);
  // Uploads (FormData / Blob) take longer than the default JSON timeout
  // because the body bytes themselves traverse the network. Bumping just
  // these requests avoids penalising every other call with a long timeout.
  // ``timeoutOverride`` lets long-running callers (SSE streams) opt out
  // entirely with ``timeoutMs: 0`` — the stream decides when it's done.
  const defaultTimeout = isFormData || isBlob ? 60_000 : REQUEST_TIMEOUT_MS;
  const effectiveTimeout = timeoutOverride ?? defaultTimeout;
  const timeoutId =
    effectiveTimeout > 0
      ? setTimeout(() => controller.abort(), effectiveTimeout)
      : null;

  // Caller-supplied signal cancels the internal controller too — so a
  // caller-driven abort runs through the same _activeControllers
  // cleanup path as a timeout / logout abort.
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener("abort", () => controller.abort(), {
        once: true,
      });
    }
  }

  const fetchInit: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: "include", // send HTTPOnly cookies
    signal: controller.signal,
  };
  if (body !== undefined) {
    if (isFormData || isBlob) {
      fetchInit.body = body as BodyInit;
    } else {
      fetchInit.body = JSON.stringify(body);
    }
  }

  let response: Response;
  try {
    response = await fetch(url, fetchInit);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      // Distinguish logout-aborts from idle-timeout-aborts so callers can
      // suppress error toasts on logout.
      if (getLogoutEpoch() !== startEpoch) {
        throw new APIClientError(0, "logged_out", "Request aborted: user logged out");
      }
      throw new APIClientError(0, "timeout", "Request timed out");
    }
    throw err;
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
    _activeControllers.delete(controller);
  }

  // Always pick up new CSRF tokens (e.g. after MFA verify reissues tokens)
  extractCSRFToken(response);

  // Auto-refresh on 401
  if (response.status === 401 && !skipRefresh) {
    // If logout happened while this fetch was in flight, the response we
    // got back is moot — abort instead of refreshing/retrying.
    if (getLogoutEpoch() !== startEpoch) {
      throw new APIClientError(0, "logged_out", "Request aborted: user logged out");
    }
    const outcome = await attemptRefresh();
    // Recheck after the refresh completes — logout may have happened while
    // we were holding the lock. Retrying with new cookies after logout
    // would silently re-establish state the user just tore down.
    if (getLogoutEpoch() !== startEpoch) {
      throw new APIClientError(0, "logged_out", "Request aborted: user logged out");
    }
    if (outcome.ok) {
      // Retry original request once
      return rawFetch(path, { ...options, skipRefresh: true });
    }
    // Refresh failed — redirect to login (replace to avoid back-button loops).
    // The backend's AuthError code is threaded through as ?reason= so the
    // login page can show an accurate, specific message (idle vs takeover
    // vs company deactivated) instead of a generic "session ended".
    if (typeof window !== "undefined") {
      const already = window.location.pathname === "/login";
      if (!already) {
        const reason = encodeURIComponent(outcome.reason);
        window.location.replace(`/login?reason=${reason}`);
      }
    }
  }

  // Auto-refresh on 403 csrf_error.
  //
  // The CSRF token is HMAC(secret, access_token), so it is invalidated
  // whenever the access token rotates. Common ways for the in-memory
  // token to drift out of sync with the cookie:
  //
  // - Cross-tab refresh: tab A's refresh rotates the cookie globally;
  //   tab B's _csrfToken is now an HMAC of the *previous* access token.
  // - Cold page load with a session restored from cookies — the auth
  //   context calls /auth/me, which doesn't issue a CSRF header, so
  //   _csrfToken stays null until the first refresh.
  //
  // A refresh issues a fresh access_token cookie *and* emits a new
  // X-CSRF-Token header that extractCSRFToken() picks up, so retrying
  // once after a successful refresh succeeds. If the refresh itself
  // fails the user has lost their session — fall through to the 401
  // handler's redirect logic by re-issuing the original request, which
  // will now hit 401 with no skipRefresh.
  if (response.status === 403 && !skipRefresh && method !== "GET" && method !== "HEAD") {
    let errCode: string | undefined;
    try {
      // Clone so the original response body remains readable by callers
      // if we end up returning it (refresh failed, no retry).
      const peek = response.clone();
      const body = (await peek.json()) as { error?: string };
      errCode = body.error;
    } catch {
      // Non-JSON 403 (e.g. plain text from a Next.js route guard) — leave
      // errCode undefined so we don't trigger refresh on unrelated 403s.
    }
    if (errCode === "csrf_error") {
      if (getLogoutEpoch() !== startEpoch) {
        throw new APIClientError(0, "logged_out", "Request aborted: user logged out");
      }
      const outcome = await attemptRefresh();
      if (getLogoutEpoch() !== startEpoch) {
        throw new APIClientError(0, "logged_out", "Request aborted: user logged out");
      }
      if (outcome.ok) {
        return rawFetch(path, { ...options, skipRefresh: true });
      }
      // Refresh failed — fall through and let the original 403 propagate.
    }
  }

  // Other 403 enforcement errors (password_change_required, mfa_setup_required)
  // are NOT handled here — they propagate as APIClientError and are handled
  // by the auth context / ProtectedRoute to avoid hard window.location redirects
  // that cause page flashing.

  return response;
}

// Track refresh version so concurrent callers can detect a completed refresh
const REFRESH_VERSION_KEY = "mi_refresh_version";

/**
 * Outcome of a refresh attempt. On failure, ``reason`` carries the
 * backend's AuthError.code so the login redirect can tell the user why
 * they ended up back on the sign-in page (idle, takeover, company
 * deactivated, account inactive, etc.) instead of a generic message.
 */
export type RefreshOutcome =
  | { ok: true }
  | { ok: false; reason: string };

async function doRefresh(): Promise<RefreshOutcome> {
  try {
    const response = await rawFetch("/auth/refresh", {
      method: "POST",
      skipRefresh: true,
    });
    if (response.ok) {
      extractCSRFToken(response);
      // Increment version so other tabs/callers know a refresh completed
      const v = parseInt(localStorage.getItem(REFRESH_VERSION_KEY) ?? "0", 10);
      localStorage.setItem(REFRESH_VERSION_KEY, String(v + 1));
      return { ok: true };
    }
    // Parse the specific failure reason from the backend response body.
    // The refresh endpoint returns {error, message} with the AuthError
    // code, e.g. "session_idle_timeout" vs "invalid_refresh_token".
    let reason = "session_ended";
    try {
      const body = (await response.json()) as { error?: string };
      if (typeof body.error === "string" && body.error.length > 0) {
        reason = body.error;
      }
    } catch {
      // Non-JSON body — fall back to generic reason
    }
    return { ok: false, reason };
  } catch {
    return { ok: false, reason: "network_error" };
  }
}

/**
 * Attempt a token refresh with cross-tab mutex via Web Locks API.
 * Prevents multiple tabs from refreshing simultaneously (which would
 * invalidate each other's tokens due to refresh token rotation).
 *
 * EXPORTIERT, WEIL ES GENAU EINEN WEG ZUM AKTUALISIEREN GEBEN MUSS. Der
 * AuthProvider hat sein eigenes `fetch("/api/v1/auth/refresh")` gehabt, ein
 * nacktes Browser-Fetch am Schloss vorbei. Solange nichts anderes gleichzeitig
 * lief, fiel das nicht auf. Seit der Seitenbaum verdeckt vormontiert wird,
 * laufen ein Dutzend Abfragen waehrend der Sitzungspruefung, und eine davon,
 * die in die Rotation faellt, loest ueber ihren eigenen 401-Pfad ein zweites
 * Aktualisieren aus. Zwei gleichzeitige Rotationen entwerten einander, und was
 * dabei herauskommt, ist eine Sitzung, die es nicht mehr gibt.
 */
export async function attemptRefresh(): Promise<RefreshOutcome> {
  // Record the version before we attempt, so we can detect if another
  // tab already refreshed while we were waiting for the lock.
  const versionBefore = localStorage.getItem(REFRESH_VERSION_KEY) ?? "0";

  if (typeof navigator !== "undefined" && "locks" in navigator) {
    return navigator.locks.request("mi-token-refresh", async () => {
      // Check if another tab already refreshed while we waited for the lock
      const versionNow = localStorage.getItem(REFRESH_VERSION_KEY) ?? "0";
      if (versionNow !== versionBefore) {
        return { ok: true } as const; // Someone else refreshed — our cookies are fresh
      }
      return doRefresh();
    });
  }

  // Fallback: no Web Locks (SSR or very old browser) — refresh directly
  return doRefresh();
}

// ── Typed API methods ────────────────────────────────────

/**
 * Make a typed API request with Zod schema validation.
 * Throws APIClientError on non-2xx responses.
 */
export async function api<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const response = await rawFetch(path, options);

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = null;
    }

    const errorBody = body as Record<string, unknown> | null;
    const retryAfter =
      response.status === 429
        ? (errorBody?.["retry_after"] as number | undefined)
        : undefined;
    throw new APIClientError(
      response.status,
      (errorBody?.["error"] as string) ?? "unknown",
      (errorBody?.["message"] as string) ?? `Request failed: ${response.status}`,
      body,
      retryAfter,
    );
  }

  const data: unknown = await response.json();
  return schema.parse(data);
}

/**
 * Make a void API request (no response body expected).
 * Throws APIClientError on non-2xx responses.
 */
export async function apiVoid(
  path: string,
  options: RequestOptions = {},
): Promise<void> {
  const response = await rawFetch(path, options);

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = null;
    }

    const errorBody = body as Record<string, unknown> | null;
    const retryAfter =
      response.status === 429
        ? (errorBody?.["retry_after"] as number | undefined)
        : undefined;
    throw new APIClientError(
      response.status,
      (errorBody?.["error"] as string) ?? "unknown",
      (errorBody?.["message"] as string) ?? `Request failed: ${response.status}`,
      body,
      retryAfter,
    );
  }
}

/**
 * Make a raw API request — returns the response directly.
 * Used when you need to inspect status codes (e.g. login flow with 409/403).
 */
export async function apiRaw(
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  return rawFetch(path, { ...options, skipRefresh: true });
}

/**
 * Make a raw authenticated request with automatic 401 refresh + retry.
 * Returns the Response directly (use for file downloads).
 */
export async function apiFetch(
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  return rawFetch(path, options);
}
